/**
 * ProjectsPage — list, create, rename, archive.
 *
 * Reads via supabase-js (RLS); mutations via FastAPI (authoritative).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderKanban, Plus, Archive, RotateCcw, Edit3, X, Check, Loader2, Search } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import {
    listProjects, createProject, updateProject, archiveProject, unarchiveProject,
} from "@/lib/projects";
import { useActiveProject } from "@/context/ActiveProjectContext";

function formatDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return iso.slice(0, 10); }
}

function CreateRow({ onCreated }) {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        setBusy(true);
        try {
            const created = await createProject({ name, description });
            setName(""); setDescription("");
            setOpen(false);
            onCreated?.(created);
        } catch (err) {
            setError(err?.detail?.detail || err?.message || "Failed to create project.");
        } finally {
            setBusy(false);
        }
    };

    if (!open) {
        return (
            <button
                type="button"
                data-testid="new-project-btn"
                onClick={() => setOpen(true)}
                className="w-full qoryn-panel rounded-2xl px-5 py-4 flex items-center gap-3 text-qoryn-textDim hover:text-qoryn-cyan hover:bg-white/[0.04] transition-colors qoryn-focus"
            >
                <span className="flex items-center justify-center h-8 w-8 rounded-lg bg-white/[0.04] text-qoryn-cyan/70">
                    <Plus className="h-4 w-4" strokeWidth={1.75} />
                </span>
                <span className="font-sans text-[14px]">New project</span>
            </button>
        );
    }

    return (
        <form
            onSubmit={submit}
            data-testid="new-project-form"
            className="qoryn-panel rounded-2xl p-5 space-y-3"
        >
            <div>
                <label className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                    Name
                </label>
                <input
                    data-testid="new-project-name"
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    minLength={1}
                    maxLength={80}
                    placeholder="e.g. Launch Q1 Marketing"
                    className="mt-1.5 w-full h-10 px-3 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none font-sans text-[14px] text-qoryn-text placeholder:text-qoryn-textMuted transition-colors qoryn-focus"
                />
            </div>
            <div>
                <label className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                    Description (optional)
                </label>
                <textarea
                    data-testid="new-project-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={500}
                    rows={2}
                    placeholder="What is this project for?"
                    className="mt-1.5 w-full px-3 py-2 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none font-sans text-[13px] text-qoryn-text placeholder:text-qoryn-textMuted transition-colors resize-none qoryn-focus"
                />
            </div>
            {error && (
                <div className="rounded-md border border-red-500/40 bg-red-500/[0.06] px-3 py-2 font-mono-ui text-[10px] tracking-wider text-red-300" data-testid="new-project-error">
                    {String(error)}
                </div>
            )}
            <div className="flex items-center gap-2 pt-1">
                <button
                    type="submit"
                    data-testid="new-project-submit"
                    disabled={busy || !name.trim()}
                    className="h-9 px-4 rounded-md bg-qoryn-cyan text-black font-mono-ui text-[10px] tracking-widest uppercase hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all qoryn-focus"
                >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Create"}
                </button>
                <button
                    type="button"
                    onClick={() => { setOpen(false); setName(""); setDescription(""); setError(null); }}
                    className="h-9 px-3 rounded-md border border-qoryn-border text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.04] font-mono-ui text-[10px] tracking-widest uppercase transition-colors qoryn-focus"
                >
                    Cancel
                </button>
            </div>
        </form>
    );
}

function ProjectRow({ project, onArchive, onUnarchive, onRename, active }) {
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(project.name);
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();
    const { setActiveId } = useActiveProject();

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || name === project.name) { setEditing(false); return; }
        setBusy(true);
        try { await onRename(project.id, name.trim()); setEditing(false); }
        finally { setBusy(false); }
    };

    return (
        <div
            data-testid={`project-row-${project.slug}`}
            data-project-status={project.status}
            className={`qoryn-panel rounded-2xl px-5 py-4 flex items-center gap-4 group transition-colors ${
                project.status === "archived" ? "opacity-70" : ""
            }`}
        >
            <span className={`flex items-center justify-center h-8 w-8 rounded-lg bg-white/[0.04] shrink-0 ${
                active ? "text-qoryn-cyan" : "text-qoryn-textDim"
            }`}>
                <FolderKanban className="h-4 w-4" strokeWidth={1.75} />
            </span>
            <div className="flex-1 min-w-0">
                {editing ? (
                    <form onSubmit={submit} className="flex items-center gap-2">
                        <input
                            data-testid={`project-rename-input-${project.slug}`}
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={80}
                            className="flex-1 h-8 px-2 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none font-sans text-[14px] text-qoryn-text qoryn-focus"
                        />
                        <button
                            type="submit"
                            data-testid={`project-rename-save-${project.slug}`}
                            disabled={busy}
                            className="h-8 w-8 flex items-center justify-center rounded-md bg-qoryn-cyan text-black hover:brightness-110 disabled:opacity-50 qoryn-focus"
                        >
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" strokeWidth={2} />}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setEditing(false); setName(project.name); }}
                            className="h-8 w-8 flex items-center justify-center rounded-md text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.04] qoryn-focus"
                        >
                            <X className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                    </form>
                ) : (
                    <button
                        type="button"
                        data-testid={`project-open-${project.slug}`}
                        onClick={() => navigate(`/projects/${project.id}`)}
                        className="text-left w-full min-w-0 group/name"
                    >
                        <div className="flex items-center gap-2">
                            <span className="font-sans text-[15px] text-qoryn-text truncate group-hover/name:text-qoryn-cyan transition-colors">
                                {project.name}
                            </span>
                            {active && (
                                <span className="font-mono-ui text-[8px] tracking-widest uppercase text-qoryn-cyan bg-qoryn-cyan/10 rounded-full px-1.5 py-0.5">
                                    Active
                                </span>
                            )}
                            {project.status === "archived" && (
                                <span className="font-mono-ui text-[8px] tracking-widest uppercase text-qoryn-textMuted bg-white/[0.04] rounded-full px-1.5 py-0.5">
                                    Archived
                                </span>
                            )}
                        </div>
                        {project.description && (
                            <span className="block font-sans text-[12px] text-qoryn-textDim truncate mt-0.5">
                                {project.description}
                            </span>
                        )}
                    </button>
                )}
            </div>
            <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                {project.status === "active" && !active && (
                    <button
                        type="button"
                        data-testid={`project-set-active-${project.slug}`}
                        onClick={() => setActiveId(project.id)}
                        title="Set as active project"
                        className="h-8 px-2 rounded-md text-qoryn-textDim hover:text-qoryn-cyan hover:bg-white/[0.04] font-mono-ui text-[9px] tracking-widest uppercase qoryn-focus"
                    >
                        Make active
                    </button>
                )}
                {!editing && (
                    <button
                        type="button"
                        data-testid={`project-rename-${project.slug}`}
                        onClick={() => setEditing(true)}
                        title="Rename"
                        className="h-8 w-8 flex items-center justify-center rounded-md text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.04] qoryn-focus"
                    >
                        <Edit3 className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                )}
                {project.status === "active" ? (
                    <button
                        type="button"
                        data-testid={`project-archive-${project.slug}`}
                        onClick={() => onArchive(project.id)}
                        title="Archive"
                        className="h-8 w-8 flex items-center justify-center rounded-md text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.04] qoryn-focus"
                    >
                        <Archive className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                ) : (
                    <button
                        type="button"
                        data-testid={`project-unarchive-${project.slug}`}
                        onClick={() => onUnarchive(project.id)}
                        title="Restore"
                        className="h-8 w-8 flex items-center justify-center rounded-md text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.04] qoryn-focus"
                    >
                        <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                )}
            </div>
            <div className="shrink-0 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted w-24 text-right">
                {formatDate(project.updated_at)}
            </div>
        </div>
    );
}

export default function ProjectsPage() {
    const { projects, activeId, refresh } = useActiveProject();
    const [query, setQuery] = useState("");
    const [showArchived, setShowArchived] = useState(false);

    const handleCreated = useCallback(() => refresh(), [refresh]);

    const handleRename = useCallback(async (id, name) => {
        await updateProject(id, { name });
        refresh();
    }, [refresh]);

    const handleArchive = useCallback(async (id) => {
        await archiveProject(id);
        refresh();
    }, [refresh]);

    const handleUnarchive = useCallback(async (id) => {
        await unarchiveProject(id);
        refresh();
    }, [refresh]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return projects
            .filter((p) => showArchived || p.status !== "archived")
            .filter((p) => !q || p.name.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q));
    }, [projects, query, showArchived]);

    return (
        <div data-testid="projects-page" className="space-y-6">
            <div className="flex items-baseline gap-4 flex-wrap">
                <h1 className="font-display text-[28px] font-light tracking-tight text-qoryn-text">Projects</h1>
                <p className="font-sans text-[13px] text-qoryn-textDim">
                    Containers for thinking, deciding, and remembering. RLS-scoped to you.
                </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
                <div className="qoryn-panel rounded-full flex items-center gap-2 px-3 h-9 flex-1 min-w-[220px] max-w-md">
                    <Search className="h-3.5 w-3.5 text-qoryn-textMuted" strokeWidth={1.75} />
                    <input
                        data-testid="projects-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search projects"
                        className="flex-1 bg-transparent border-0 outline-none font-sans text-[13px] text-qoryn-text placeholder:text-qoryn-textMuted"
                    />
                </div>
                <label className="flex items-center gap-2 font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim cursor-pointer qoryn-focus">
                    <input
                        data-testid="projects-show-archived"
                        type="checkbox"
                        checked={showArchived}
                        onChange={(e) => setShowArchived(e.target.checked)}
                        className="accent-qoryn-cyan"
                    />
                    Show archived
                </label>
            </div>

            <CreateRow onCreated={handleCreated} />

            <div className="space-y-2" data-testid="projects-list">
                {filtered.length === 0 && projects.length === 0 && (
                    <EmptyState
                        icon={FolderKanban}
                        title="No projects yet."
                        body="Every new user starts with 'Qoryn OS' seeded. If it's missing, contact support — otherwise create your first project above."
                    />
                )}
                {filtered.length === 0 && projects.length > 0 && (
                    <div className="qoryn-panel rounded-2xl px-5 py-4 font-sans text-[13px] text-qoryn-textDim text-center">
                        No matches for &ldquo;{query}&rdquo;.
                    </div>
                )}
                {filtered.map((p) => (
                    <ProjectRow
                        key={p.id}
                        project={p}
                        active={p.id === activeId}
                        onArchive={handleArchive}
                        onUnarchive={handleUnarchive}
                        onRename={handleRename}
                    />
                ))}
            </div>
        </div>
    );
}
